<template>
  <settings-group>
    <settings-group-item label="Review Title">
      <v-text-field
        v-if="!disabled"
        class="mb-6"
        :value="value.title"
        :rules="titleRules"
        hide-details="auto"
        data-testid="title-text-field"
        autofocus
        @input="onTitleSet"
      />
      <div v-else class="pb-8">{{ value.title }}</div>
    </settings-group-item>
    <settings-group-item label="Review Type">
      <div
        v-if="!disabled"
        class="review-type rounded d-flex justify-center grey--text text--darken-2 font-weight-medium mb-4 pa-3"
      >
        {{ reviewTypeTitle || 'None Selected' }}
      </div>
      <div v-else>{{ reviewTypeTitle || 'None' }}</div>
      <action-button
        v-if="!disabled"
        color="primary"
        class="mr-3"
        @click="showReviewTypeDialog = true"
      >
        {{
          value.reviewType
            ? 'Change Review Type'
            : 'Select Review Type'
        }}
      </action-button>
      <review-type-dialog
        :open.sync="showReviewTypeDialog"
        :review="value"
        @update-review-type="onReviewTypeSet"
      />
    </settings-group-item>
    <settings-group-item label="Living Review">
      <v-switch
        v-if="!disabled"
        class="mt-0"
        :value="value.livingReview"
        :input-value="value.livingReview"
        @change="onLivingReviewSet"
      />
      <div v-else>{{ value.livingReview ? 'Yes' : 'No' }}</div>
    </settings-group-item>
  </settings-group>
</template>

<script>
import { Review, Researcher } from '@/models';
import { rules } from '@/modules';
import ReviewTypeDialog from './ReviewSettingsPage/ReviewTypeDialog';
import { reviewTypeToInfoMapping } from '@/modules/reviewTypeMappings';
import ActionButton from '@/components/Common/ActionButton';
import SettingsGroup from './ReviewSettingsPage/SettingsGroup';
import SettingsGroupItem from './ReviewSettingsPage/SettingsGroupItem';

export default {
  name: 'ReviewForm',
  components: {
    ReviewTypeDialog,
    ActionButton,
    SettingsGroup,
    SettingsGroupItem,
  },
  props: {
    value: {
      type: Review,
      default: () => new Review(),
    },
    updateDebounce: {
      type: Number,
      default: 500,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
  },
  emits: ['input'],
  data: () => ({
    debounceTimeout: null,
    showReviewTypeDialog: false,
  }),
  computed: {
    titleRules() {
      return [rules.whitespaceOrEmpty('Title is required')];
    },
    updating() {
      return !!this.value.id;
    },
    reviewTypeTitle() {
      return this.value.reviewType
        ? reviewTypeToInfoMapping[this.value.reviewType].title
        : '';
    },
    disabled() {
      return (
        !this.currentResearcher.isSuperAdmin &&
        this.value.leadReviewer.researcher.id !==
          this.currentResearcher.id
      );
    },
  },
  methods: {
    onTitleSet(title) {
      const review = new Review(this.value);
      review.title = title;
      this.$emit('input', review);
      this.triggerUpdateEvent();
    },
    onReviewTypeSet(reviewType) {
      const review = new Review(this.value);
      review.reviewType = reviewType;
      this.$emit('input', review);
      this.triggerUpdateEvent();
    },
    onLivingReviewSet(livingReview) {
      const review = new Review(this.value);
      review.livingReview = livingReview ?? false;
      this.$emit('input', review);
      this.triggerUpdateEvent();
    },
    validateRules(rules, value) {
      return rules.every((rule) => rule(value) === true);
    },
    valid() {
      return this.validateRules(this.titleRules, this.value.title);
    },
    triggerUpdateEvent() {
      // Only trigger update events when the review is being edited.
      if (!this.updating) return;

      // Trigger update events after a debounce delay.
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        if (this.valid()) this.$emit('update');
      }, this.updateDebounce);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';
.review-type {
  width: 190px;
  border: 2px solid;
  border-color: map-get($grey, 'lighten-1');
  background-color: map-get($grey, 'lighten-5');
}
</style>
